import ShelfDemo from './ShelfDemo.jsx';
import { useRevealProps } from './reveal.js';
import { useSignedIn } from './session.js';
import { ADMIN_URL } from './config.js';

// The spines in the hero. Scenery, not data - a real display takes its colours
// from the actual book covers.
//
// The widths matter as much as the colours. Nine identical bars is a chart;
// books are all slightly different, one of them leans, and a couple are lying
// flat on top because that is what a shelf someone uses looks like.
const HERO_SPINES = [
  { color: '#f4325e', height: 92, width: 38 },
  { color: '#ffc93c', height: 78, width: 24 },
  { color: '#35b9e8', height: 96, width: 33 },
  { color: '#8b45d9', height: 84, width: 19 },
  { color: '#ff7a33', height: 71, width: 44 },
  { color: '#3fc45f', height: 90, width: 23 },
  { color: '#ff6bb3', height: 76, width: 35 },
  { color: '#35b9e8', height: 88, width: 27 },
  { color: '#f4325e', height: 81, width: 40, lean: true },
];

const REASONS = [
  {
    title: 'You have already done the work',
    body: 'Years of books, already logged on Goodreads. Litbyt reads that list and turns it into something worth looking at, instead of a page you visit twice a year.',
  },
  {
    title: 'It keeps itself up to date',
    body: 'Finish a book, mark it read, and it appears on the shelf. Nothing to upload, nothing to tidy, nothing to remember on a Sunday evening.',
  },
  {
    title: 'Built for the room, not the desk',
    body: 'Big covers, warm light and lettering you can read from the sofa. It looks like furniture, not like a web page somebody left open.',
  },
];

const STEPS = [
  {
    title: 'Create a free account',
    body: 'Give us an email address and we send you a link. Click it and you are in. There is no password to invent, and none to forget six months from now.',
    note: 'Takes about a minute',
  },
  {
    title: 'Add your Goodreads profile',
    body: 'Paste in your Goodreads profile and Litbyt goes and fetches your shelf — titles, authors, covers and the ratings you gave them. After that it checks back on its own.',
    note: 'One paste, then it is hands-off',
  },
  {
    title: 'Share your bookshelf link',
    body: 'You get a link to your shelf. Send it to a friend, put it in your profile, or open it on any screen in the house. Whoever you send it to just sees your books — they never need an account of their own.',
    note: 'Works in any browser',
  },
  {
    title: 'Put it on the television',
    body: 'Open Litbyt on your TV and it shows a square code on screen. Point your phone camera at it, and that television is yours — quietly refreshing itself from then on, with nothing left to set up.',
    note: 'Optional',
    optional: true,
  },
];

/** The main call to action, which changes its mind once we know who is here. */
function PrimaryCTA({ signedIn, size = '' }) {
  // Held blank until the answer arrives, rather than guessing and then swapping
  // the words out from under a cursor that is already on its way.
  const pending = signedIn === null;
  const label = signedIn ? 'Open your bookshelf' : 'Get started — it’s free';

  return (
    <a
      className={`cta ${size} ${pending ? 'is-pending' : ''}`.trim()}
      href={ADMIN_URL}
      aria-label={pending ? 'Continue to Litbyt' : label}
    >
      <span className="cta-label">{pending ? 'Continue to Litbyt' : label}</span>
      <span className="cta-arrow" aria-hidden="true">→</span>
    </a>
  );
}

function Header({ signedIn }) {
  return (
    <header className="site-header">
      <a className="wordmark" href="/">
        <span className="wordmark-mark" aria-hidden="true">
          <i style={{ background: '#f4325e' }} />
          <i style={{ background: '#ffc93c' }} />
          <i style={{ background: '#35b9e8' }} />
        </span>
        Litbyt
      </a>
      <nav className="site-nav">
        <a className="text-link nav-link" href="#how">
          How it works
        </a>
        <a className="text-link nav-link" href={ADMIN_URL}>
          {signedIn ? 'Your shelf' : 'Sign in'}
        </a>
        <PrimaryCTA signedIn={signedIn} size="cta-small" />
      </nav>
    </header>
  );
}

function Hero({ signedIn }) {
  return (
    <section className="hero">
      <div className="hero-copy">
        <p className="eyebrow">A shelf for the room you actually sit in</p>
        <h1>Your books, on the biggest screen in the house.</h1>
        <p className="lede">
          Litbyt takes the reading you have already logged and turns it into a bookshelf — one you
          can leave up on a television, or hand to a friend as a link.
        </p>
        <div className="hero-actions">
          <PrimaryCTA signedIn={signedIn} size="cta-large" />
          <a className="ghost-button" href="#shelf">
            See a real one
          </a>
        </div>
        <p className="fine-print">Free to start · No card · Your Goodreads list stays yours</p>
      </div>

      <div className="hero-art" aria-hidden="true">
        <div className="hero-shelf">
          <div className="hero-backboard" />
          <div className="hero-spines">
            {HERO_SPINES.map((spine, index) => (
              <span
                key={spine.color}
                className={spine.lean ? 'hero-spine is-leaning' : 'hero-spine'}
                style={{
                  // The colour is a custom property rather than the background
                  // itself: the bands and the gloss down each spine are drawn
                  // over it in CSS, and a plain background would paint them out.
                  '--spine': spine.color,
                  height: `${spine.height}%`,
                  width: `${spine.width}px`,
                  // Each spine drops in a beat after the one to its left, so
                  // the shelf fills rather than appearing all at once.
                  animationDelay: `${0.35 + index * 0.06}s`,
                }}
              />
            ))}
            {/* Two lying flat in the gap the leaning book leaves. */}
            <span className="hero-stack">
              <i style={{ '--spine': '#3fc45f' }} />
              <i style={{ '--spine': '#ffc93c' }} />
            </span>
          </div>
          <div className="hero-plank" />
        </div>
        <div className="hero-glow" />
      </div>
    </section>
  );
}

function Reasons() {
  const reveal = useRevealProps('reasons');

  return (
    <section {...reveal}>
      {REASONS.map((reason, index) => (
        <article className="reason" key={reason.title} style={{ transitionDelay: `${index * 90}ms` }}>
          <h2>{reason.title}</h2>
          <p>{reason.body}</p>
        </article>
      ))}
    </section>
  );
}

function ShelfSection() {
  const reveal = useRevealProps('shelf-inner');

  return (
    <section className="shelf-section" id="shelf">
      <div {...reveal}>
        <p className="eyebrow eyebrow-light">Live, right now</p>
        <h2>This is somebody’s actual shelf.</h2>
        <ShelfDemo />
      </div>
    </section>
  );
}

function Steps({ signedIn }) {
  const heading = useRevealProps('section-head');

  return (
    <section className="steps-section" id="how">
      <div {...heading}>
        <p className="eyebrow">How it works</p>
        <h2>Four small steps, and the last one is optional.</h2>
      </div>

      <ol className="steps">
        {STEPS.map((step, index) => (
          <Step key={step.title} step={step} index={index} />
        ))}
      </ol>

      <div className="steps-cta">
        <PrimaryCTA signedIn={signedIn} size="cta-large" />
      </div>
    </section>
  );
}

function Step({ step, index }) {
  const reveal = useRevealProps(`step ${step.optional ? 'is-optional' : ''}`.trim());

  return (
    <li {...reveal}>
      <div className="step-number" aria-hidden="true">
        {index + 1}
      </div>
      <div className="step-body">
        <h3>{step.title}</h3>
        <p>{step.body}</p>
      </div>
      {/* The aside carries what the step costs you, and on the optional step
          the square a television actually shows. It is a column of its own so
          a wide row is used rather than trailing off into white space. */}
      <div className="step-aside">
        {step.optional && <QrMark />}
        <p className="step-note">{step.note}</p>
      </div>
    </li>
  );
}

// The square a television shows when it is waiting to be claimed. Drawn rather
// than generated, because a real scannable code here would point at a pairing
// that does not exist - this is the shape, so the step is recognisable when
// somebody meets it for real.
function QrMark() {
  return (
    <svg className="qr-mark" viewBox="0 0 29 29" aria-hidden="true">
      <rect width="29" height="29" rx="3" className="qr-ground" />
      {[
        [3, 3],
        [19, 3],
        [3, 19],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect x={x} y={y} width="7" height="7" rx="1" className="qr-eye" />
          <rect x={x + 2} y={y + 2} width="3" height="3" className="qr-pupil" />
        </g>
      ))}
      {[
        [12, 4], [14, 4], [12, 7], [15, 8], [13, 10], [17, 11], [4, 12], [7, 13],
        [10, 12], [12, 12], [16, 13], [19, 12], [22, 13], [24, 14], [5, 15],
        [8, 16], [11, 15], [14, 16], [18, 16], [21, 17], [24, 18], [12, 19],
        [15, 20], [19, 20], [22, 21], [13, 22], [17, 23], [20, 24], [24, 22],
        [12, 25], [16, 25], [22, 25],
      ].map(([x, y]) => (
        <rect key={`${x}.${y}`} x={x} y={y} width="2" height="2" className="qr-cell" />
      ))}
    </svg>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <span className="wordmark wordmark-quiet">Litbyt</span>
      <nav className="footer-nav">
        <a className="text-link" href={ADMIN_URL}>
          Sign in
        </a>
        <a className="text-link" href="#how">
          How it works
        </a>
      </nav>
      <p className="fine-print">Books, on the wall where they belong.</p>
    </footer>
  );
}

export default function WwwApp() {
  const signedIn = useSignedIn();

  return (
    <>
      <Header signedIn={signedIn} />
      <main>
        <Hero signedIn={signedIn} />
        <Reasons />
        <ShelfSection />
        <Steps signedIn={signedIn} />
      </main>
      <Footer />
    </>
  );
}
